import { memo } from "react";
import { animated, to, useSpring } from "@react-spring/web";
import { EdgeLabelRenderer, type EdgeProps } from "@xyflow/react";
import type { HealthMapFlowEdge } from "./healthMapUtils";

export const HealthMapSpringEdge = memo(function HealthMapSpringEdge({
  data,
  id,
  label,
  markerEnd,
  selected,
  sourceX,
  sourceY,
  style,
  targetX,
  targetY
}: EdgeProps<HealthMapFlowEdge>) {
  const motion = data?.motion;
  const spring = useSpring({
    config: motion?.active
      ? { tension: 250, friction: 17, mass: 0.9 }
      : { tension: 180, friction: 13, mass: 1 },
    sourceX,
    sourceY,
    targetX,
    targetY
  });
  const stroke = typeof style?.stroke === "string" ? style.stroke : "rgba(136, 160, 152, 0.58)";
  const strokeWidth = Number(style?.strokeWidth || (data?.unhealthy ? 2.2 : 1.4));
  const motionIntensity = motion?.connected ? motion.intensity : 0;
  const labelDepth = motion?.connected ? Math.round(16 + motionIntensity * 28) : 0;
  const path = to([spring.sourceX, spring.sourceY, spring.targetX, spring.targetY], (sx, sy, tx, ty) =>
    buildSpringPath(sx, sy, tx, ty, motion?.vectorX || 0, motion?.vectorY || 0, motionIntensity)
  );
  const labelTransform = to([spring.sourceX, spring.sourceY, spring.targetX, spring.targetY], (sx, sy, tx, ty) => {
    const labelX = sx + (tx - sx) / 2;
    const labelY = sy + (ty - sy) / 2;
    return `translate(-50%, -50%) translate3d(${labelX}px, ${labelY}px, ${labelDepth}px)`;
  });

  return (
    <>
      <animated.path
        className={[
          "health-flow-edge-glow",
          motion?.connected ? "motion-connected" : "",
          selected ? "selected" : ""
        ].filter(Boolean).join(" ")}
        d={path}
        style={{
          opacity: data?.unhealthy ? 0.18 + motionIntensity * 0.42 : motionIntensity * 0.5,
          stroke,
          strokeWidth: strokeWidth + 9 + motionIntensity * 5
        }}
      />
      <animated.path
        className={[
          "react-flow__edge-path",
          "health-flow-edge-path",
          motion?.connected ? "motion-connected" : "",
          data?.unhealthy ? "unhealthy" : "",
          selected ? "selected" : ""
        ].filter(Boolean).join(" ")}
        d={path}
        id={id}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke,
          strokeWidth: strokeWidth + motionIntensity * 2.6
        }}
      />
      <EdgeLabelRenderer>
        <animated.div
          className={[
            "health-flow-edge-label",
            motion?.connected ? "motion-connected" : "",
            data?.unhealthy ? "unhealthy" : ""
          ].filter(Boolean).join(" ")}
          style={{
            borderColor: data?.unhealthy || motion?.connected ? stroke : undefined,
            color: data?.unhealthy || motion?.connected ? stroke : undefined,
            transform: labelTransform
          }}
        >
          {label || data?.relationshipLabel}
        </animated.div>
      </EdgeLabelRenderer>
    </>
  );
});

function buildSpringPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  vectorX: number,
  vectorY: number,
  intensity: number
): string {
  const deltaX = targetX - sourceX;
  const deltaY = targetY - sourceY;
  const distance = Math.max(1, Math.hypot(deltaX, deltaY));
  const normalX = -deltaY / distance;
  const normalY = deltaX / distance;
  const pull = Math.min(110, Math.hypot(vectorX, vectorY) * 4.2 * intensity);
  const directionalPull = (vectorX * normalX + vectorY * normalY) * 1.2;
  const bend = pull + directionalPull;
  const controlX1 = sourceX + deltaX * 0.28 + normalX * bend;
  const controlY1 = sourceY + deltaY * 0.15 + normalY * bend;
  const controlX2 = targetX - deltaX * 0.28 + normalX * bend * 0.72;
  const controlY2 = targetY - deltaY * 0.15 + normalY * bend * 0.72;

  return `M ${sourceX} ${sourceY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${targetX} ${targetY}`;
}
